import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import axios from 'axios';
import { config } from '../../config';
import { getUnsignedTenants } from '../../utils/Permissions';
import { TermsAndConditionsContentInvestCEO } from '../../docs/terms/investceo/latest';
import { TermsAndConditionsContentALM } from '../../docs/terms/alm/latest';
import { TermsAndConditionsContentOptionGenius } from '../../docs/terms/optiongenius/latest';
import { PrivacyPolicyContentALM } from '../../docs/privacy/alm/latest';

const TENANT_TERMS = {
    investceo: TermsAndConditionsContentInvestCEO,
    optiongenius: TermsAndConditionsContentOptionGenius,
    alm: TermsAndConditionsContentALM,
};

const getTermsForTenant = (tenant) => {
    return {
        terms: TENANT_TERMS[tenant] || TENANT_TERMS.alm,
        privacy: PrivacyPolicyContentALM,
    };
};

export const TermsAcceptancePage = ({ permissions, onAccepted }) => {
    const { getAccessTokenSilently, user } = useAuth0();
    const navigate = useNavigate();

    const unsignedTenants = getUnsignedTenants(permissions);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTermsChecked, setIsTermsChecked] = useState(false);
    const [isRiskChecked, setIsRiskChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (unsignedTenants.length === 0) {
        return null;
    }

    const currentTenant = unsignedTenants[currentIndex];
    const { terms: TermsContent, privacy: PrivacyContent } = getTermsForTenant(currentTenant);

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const userId = user.sub.split('|')[1];
            const token = await getAccessTokenSilently();
            const response = await axios.post(
                `${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/permissions`,
                {
                    tenant: currentTenant,
                    termsAndConditionsVersion: config.latestTermsAndConditionsVersion,
                    privacyPolicyVersion: config.latestPrivacyPolicyVersion,
                    timestamp: new Date(),
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { uid: userId },
                }
            );

            if (response && response.data === 'OK') {
                setIsTermsChecked(false);
                setIsRiskChecked(false);

                if (currentIndex + 1 < unsignedTenants.length) {
                    setCurrentIndex(currentIndex + 1);
                } else {
                    if (onAccepted) {
                        onAccepted();
                    }
                    navigate('/connect');
                }
            } else {
                setError('Error saving acceptance. Please try again.');
            }
        } catch (err) {
            setError(err.message || 'Error saving acceptance. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4" style={{ maxWidth: '900px' }}>
            {unsignedTenants.length > 1 && (
                <p className="text-muted mb-3">
                    Agreement {currentIndex + 1} of {unsignedTenants.length}
                </p>
            )}

            <h2 className="mb-3">Terms & Conditions</h2>
            <div style={{ height: '300px', overflowY: 'scroll', border: '1px solid #dee2e6', borderRadius: '6px', padding: '1rem', backgroundColor: '#fafafa' }}>
                <TermsContent />
            </div>

            <h2 className="mt-4 mb-3">Privacy Policy</h2>
            <div style={{ height: '300px', overflowY: 'scroll', border: '1px solid #dee2e6', borderRadius: '6px', padding: '1rem', backgroundColor: '#fafafa' }}>
                <PrivacyContent />
            </div>

            <div className="mt-4">
                <Form.Check
                    className="mb-3 p-3 ps-5"
                    style={{ border: '2px solid #dc3545', borderRadius: '6px', cursor: 'pointer' }}
                    type="checkbox"
                    id="termsCheckbox"
                    label="By checking this box, I acknowledge that I have read, understood, and agree to be bound by the Terms & Conditions and the Privacy Policy above."
                    checked={isTermsChecked}
                    onChange={(e) => setIsTermsChecked(e.target.checked)}
                />
                <Form.Check
                    className="mb-3 p-3 ps-5"
                    style={{ border: '2px solid #dc3545', borderRadius: '6px', cursor: 'pointer' }}
                    type="checkbox"
                    id="riskCheckbox"
                    label="I understand that trading involves significant risk of loss, that past performance is not indicative of future results, and that ALM Trading and your Licensor do not provide investment advice."
                    checked={isRiskChecked}
                    onChange={(e) => setIsRiskChecked(e.target.checked)}
                />
            </div>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

            <div className="d-flex justify-content-center mt-4">
                <Button
                    variant="primary"
                    size="lg"
                    disabled={!isTermsChecked || !isRiskChecked || loading}
                    onClick={handleSubmit}
                >
                    {loading ? 'Saving...' : 'I Accept'}
                </Button>
            </div>
        </Container>
    );
};
